/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { AiManagementCard } from 'store/modules/ai_management_store/types';
import { GestaoTypes } from 'store/modules/bidding_store/types';
import { getColumns } from 'store/modules/ai_management_store/actions';
import { createCard, listCards } from 'services/actions/ai_management_cards';
import { MANAGE_MENU_ITEMS, ManageMenuItem } from 'services/ai_management_manage_menu';
import AiManagementCardModal from 'components/organisms/ai_management_card_modal';
import createTrack from 'services/actions/tracker_actions';
import { Container } from './styles';

interface BiddingManageButtonProps {
  biddingId: string;
  source: string;
  gestao?: GestaoTypes;
}

const BiddingManageButton: React.FC<BiddingManageButtonProps> = (props) => {
  const { biddingId, source, gestao } = props;

  const dispatch = useDispatch();
  const [managed, setManaged] = useState<boolean>(gestao?.managing ?? false);
  const [members, setMembers] = useState<Array<string>>(gestao?.members ?? []);
  const [count, setCount] = useState<number>(gestao?.members?.length ?? 0);
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [card, setCard] = useState<AiManagementCard | null>(null);
  const [openingCard, setOpeningCard] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState<'new_task' | undefined>(undefined);
  const menuRef = useRef<HTMLDivElement>(null);

  const managing = gestao?.managing;
  const membersKey = gestao?.members?.join('|') ?? '';

  useEffect(() => {
    if (managing === undefined) return;
    setManaged(managing);
    setMembers(gestao?.members ?? []);
    setCount(gestao?.members?.length ?? 0);
  }, [managing, membersKey]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleManage = () => {
    if (loading) return;

    const previousManaged = managed;
    const previousCount = count;
    setManaged(true);
    setCount(Math.max(count, 1));
    setLoading(true);

    createCard(Number(biddingId)).then((res) => {
      setLoading(false);
      if (!res || (res.status >= 400 && res.status !== 422)) {
        setManaged(previousManaged);
        setCount(previousCount);
        setMenuOpen(false);
        return;
      }

      createTrack('Gestão IA - Incluiu licitação', { fonte: source });
    });
  };

  const handleMainClick = () => {
    if (managed) {
      setMenuOpen((open) => !open);
      return;
    }
    handleManage();
  };

  const openCardModal = (action?: 'new_task') => {
    setMenuOpen(false);
    setModalAction(action);
    dispatch(getColumns());

    if (card) {
      setModalOpen(true);
      return;
    }

    setOpeningCard(true);
    listCards().then((res) => {
      setOpeningCard(false);
      if (!res || res.status >= 400) return;

      const grouped = (res.data?.cards ?? {}) as Record<string, AiManagementCard[]>;
      const found = Object.values(grouped)
        .flat()
        .find((item) => item.bidding_id === Number(biddingId));
      if (!found) return;

      setCard(found);
      setModalOpen(true);
      createTrack('Gestão IA - Abriu a licitação pelo card', { fonte: source });
    });
  };

  const renderMenuItem = (item: ManageMenuItem) => {
    if (item.route) {
      return (
        <Link
          key={item.key}
          to={item.route}
          className="dropdown-item"
          onClick={() => {
            setMenuOpen(false);
            createTrack('Gestão IA - Acessou pelo card da licitação', { fonte: source });
          }}
        >
          {item.label}
        </Link>
      );
    }

    if (item.action === 'card_modal' || item.action === 'card_modal_new_task') {
      return (
        <button
          key={item.key}
          type="button"
          className="dropdown-item"
          disabled={openingCard}
          onClick={() =>
            openCardModal(item.action === 'card_modal_new_task' ? 'new_task' : undefined)
          }
        >
          {item.label}
          {openingCard && <span className="spinner-border spinner-border-sm ml-2" role="status" />}
        </button>
      );
    }

    return (
      <span key={item.key} className="dropdown-item disabled" title="Em breve">
        {item.label}
      </span>
    );
  };

  return (
    <Container managed={managed} className="dropdown mr-2 d-print-none" ref={menuRef}>
      <div className="manage-box d-flex align-items-center rounded">
        <button
          type="button"
          className="manage-main btn btn-sm d-flex align-items-center text-white text-nowrap"
          aria-expanded={menuOpen}
          onClick={handleMainClick}
        >
          <i className="material-icons manage-star mr-1">star</i>
          {managed ? 'Gerenciando' : 'Gerenciar'}
          {count > 0 && (
            <span
              className="manage-count badge badge-light d-flex align-items-center ml-2"
              title={members.join(', ')}
            >
              <i className="material-icons">person</i>
              {count}
            </span>
          )}
        </button>

        {managed && (
          <button
            type="button"
            className="manage-caret btn btn-sm d-flex align-items-center text-white"
            aria-label="Opções do Gestão IA"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <i className="material-icons">expand_more</i>
          </button>
        )}
      </div>

      {menuOpen && (
        <div className="dropdown-menu show manage-menu">
          {MANAGE_MENU_ITEMS.map(renderMenuItem)}
        </div>
      )}

      {modalOpen && card && (
        <AiManagementCardModal
          card={card}
          initialAction={modalAction}
          onClose={() => setModalOpen(false)}
        />
      )}
    </Container>
  );
};

export default BiddingManageButton;
